import { getAccessToken } from './AuthService';

export type RequestInterceptor = (url: string, config: RequestInit) => RequestInit;
export type ResponseInterceptor = (response: Response, config: RequestInit) => Response;

/**
 * The FetchInterceptor class is a singleton that intercepts fetch calls and applies custom logic to them.
 * It allows you to add request and response interceptors that will be applied to all fetch calls.
 * Rules can be added to the interceptors to determine when they should be applied.
 *
 * This is mainly used to add a refresh token mechanism to fetch calls.
 *
 * ## Justification
 *
 * We are using CouchDB through PouchDB.
 * We can't modify CouchDB responses; they will return a 401 status code on an expired JWT.
 * We can't use long-lived JWTs because they are a security risk.
 * That's a bad security practice because once a JWT is trusted, it is trusted for its entire life unequivocally.
 * If one is compromised, the only way to limit damage is to invalidate all JWTs, which is very disruptive.
 * With refresh tokens, we can simply invalidate the specific refresh token.
 * We don't want to repeat ourselves and add checks for the refresh token on every API call and handle calling the refresh token API.
 * That will lead to code repetition and maintenance issues.
 * The solution is to make the refresh mechanism transparent to the API calls.
 * We achieve that by intercepting fetch calls and adding our custom logic there,
 * which is the purpose of the FetchInterceptor.
 */
export class InterceptorRule {
  urlPattern: string | RegExp;
  statusCodes: number[];
  customFunction: (arg: Request | Response) => boolean;

  constructor(urlPattern: string | RegExp = /.*/, statusCodes: number[] = [], customFunction: (arg: Request | Response) => boolean = () => true) {
    this.urlPattern = urlPattern;
    this.statusCodes = statusCodes;
    this.customFunction = customFunction;
  }

  matches(arg: Request | Response): boolean {
    const statusMatches = 'status' in arg ? this.statusCodes.includes(arg.status) : true;
    if (!statusMatches) return false;

    const urlMatches = typeof this.urlPattern === 'string' ? arg.url.includes(this.urlPattern) : this.urlPattern.test(arg.url);
    if (!urlMatches) return false;

    return this.customFunction(arg);
  }
}

export class FetchInterceptor {
  private static instances: { [name: string]: FetchInterceptor } = {};
  private requestInterceptors: { rule: InterceptorRule; interceptor: RequestInterceptor }[] = [];
  private responseInterceptors: { rule: InterceptorRule; interceptor: ResponseInterceptor }[] = [];

  private constructor() {}

  public static getDefaultInstance(): FetchInterceptor {
    return this.getInstance('default');
  }

  public static getInstance(name: string): FetchInterceptor {
    if (!this.instances[name]) {
      this.instances[name] = new FetchInterceptor();
    }

    return this.instances[name];
  }

  addRequestInterceptor(interceptor: RequestInterceptor, rule: InterceptorRule = new InterceptorRule()): void {
    this.requestInterceptors.push({ rule, interceptor });
  }

  addResponseInterceptor(interceptor: ResponseInterceptor, rule: InterceptorRule = new InterceptorRule()): void {
    this.responseInterceptors.push({ rule, interceptor });
  }

  async authFetch(url: string, config: RequestInit = {}): Promise<Response> {
    //console.log("authFetch", url, new Date(Date.now()).toISOString())// temp while i figure out why sync is not working

    // Ensure headers is a plain object with normalized lowercase keys
    let headersObj: Record<string, string> = {};
    if (config.headers) {
      if (config.headers instanceof Headers) {
        config.headers.forEach((value, key) => {
          headersObj[key.toLowerCase()] = value;
        });
      } else if (Array.isArray(config.headers)) {
        config.headers.forEach(([key, value]) => {
          headersObj[key.toLowerCase()] = value;
        });
      } else {
        Object.entries(config.headers).forEach(([key, value]) => {
          headersObj[key.toLowerCase()] = value as string;
        });
      }
    }

    config.headers = headersObj;

    const requestLike = { url } as Request;

    // Apply request interceptors
    this.requestInterceptors.forEach(({ rule, interceptor }) => {
      if (rule.matches(requestLike)) {
        config = interceptor(url, config);
      }
    });

    try {
      let response = await fetch(url, config);

      for (const { rule, interceptor } of this.responseInterceptors) {
        if (rule.matches(response)) {
          response = await interceptor(response, config);
        }
      }

      return response;
    } catch (error) {
      throw new Error(`Failed to fetch [${config.method}] ${url}: ${error.message} (${error.stack})`);
    }
  }

  // Convenience methods
  async GET(url: string, config: RequestInit = {}): Promise<Response> {
    return this.authFetch(url, { ...config, method: 'GET' });
  }

  async POST(url: string, body?: any, config: RequestInit = {}): Promise<Response> {
    const requestConfig: RequestInit = { ...config, method: 'POST' };

    if (body !== undefined) {
      // If body is already a string, FormData, Blob, etc., use it directly
      // Otherwise, stringify it as JSON and set content-type header
      if (typeof body === 'string' || body instanceof FormData || body instanceof Blob || body instanceof ArrayBuffer) {
        requestConfig.body = body;
      } else {
        requestConfig.body = JSON.stringify(body);
        requestConfig.headers = {
          'content-type': 'application/json',
          ...config.headers,
        };
      }
    }

    return this.authFetch(url, requestConfig);
  }

  async PUT(url: string, body?: any, config: RequestInit = {}): Promise<Response> {
    const requestConfig: RequestInit = { ...config, method: 'PUT' };

    if (body !== undefined) {
      if (typeof body === 'string' || body instanceof FormData || body instanceof Blob || body instanceof ArrayBuffer) {
        requestConfig.body = body;
      } else {
        requestConfig.body = JSON.stringify(body);
        requestConfig.headers = {
          'content-type': 'application/json',
          ...config.headers,
        };
      }
    }

    return this.authFetch(url, requestConfig);
  }

  async DELETE(url: string, config: RequestInit = {}): Promise<Response> {
    return this.authFetch(url, { ...config, method: 'DELETE' });
  }
}
